import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ChangePasswordRequest, type UpdateMeRequest } from '@utazzo/shared';
import { useEffect } from 'react';
import { toast } from 'sonner';

import * as api from './my-profile.api';

import { useAuthStore } from '@/stores/auth-store';

const QK = {
  profile: ['me', 'profile'] as const,
};

export const useMyProfile = () =>
  useQuery({
    queryKey: QK.profile,
    queryFn: () => api.getProfile(),
    staleTime: 30_000,
  });

/**
 * Effective permission set for the signed-in user (own + inherited via
 * the role chain). Returns `undefined` while the profile is loading,
 * `null` when the user has no resolvable grants, or a `Set<string>` of
 * direct permission codes otherwise.
 *
 * Wire this into nav-item visibility + per-route guards (ADR-0039
 * Phase 3) so admin revokes in the matrix actually take effect at the
 * UI layer without a session re-login.
 */
/**
 * Reconcile the persisted session user with GET /me.
 *
 * The auth store writes `user` once, at login, and persists it to
 * localStorage. Effective permissions come from /me on every load, so the
 * sidebar reacted to an admin's changes immediately - but every `role === ...`
 * branch (dashboard layout, route guards, editor caps) kept reading the role
 * captured at login. A user moved to HR_EXECUTIVE therefore kept the sales
 * dashboard until they signed out and back in.
 *
 * Mount once, high in the authenticated tree (AppShell).
 */
export const useSyncSessionUser = (): void => {
  const profile = useMyProfile();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const fresh = profile.data?.user;

  useEffect(() => {
    if (!fresh || !user) return;
    if (
      fresh.role === user.role &&
      fresh.email === user.email &&
      fresh.fullName === user.fullName
    ) {
      return;
    }
    // /me is a profile projection, not an AuthenticatedUser - merge the fields
    // both shapes share and keep the rest of the session row intact.
    setUser({ ...user, role: fresh.role, email: fresh.email, fullName: fresh.fullName });
  }, [fresh, user, setUser]);
};

export const useMyEffectivePermissions = (): Set<string> | null | undefined => {
  const profile = useMyProfile();
  if (!profile.data) return undefined;
  const codes = profile.data.effectivePermissions;
  if (codes.length === 0) return null;
  return new Set(codes);
};

export const useUpdateMyProfile = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateMeRequest) => api.updateProfile(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.profile });
      toast.success('Profile updated.');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Could not update profile'),
  });
};

export const useChangeMyPassword = () =>
  useMutation({
    mutationFn: (payload: ChangePasswordRequest) => api.changePassword(payload),
    onSuccess: () => toast.success('Password changed.'),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Could not change password'),
  });

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
